import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ticketService } from '../services/api';
import { useSidebar } from '../context/SidebarContext';

// Map DB page_route to actual frontend route
const getFrontendRoute = (dbRoute) => {
  if (!dbRoute) return '/';
  // These routes need /admin/ prefix on the frontend
  const adminPrefixRoutes = ['/regions', '/branches', '/categories', '/teams', '/tags', '/statuses', '/escalations', '/users', '/sender-emails', '/equipment'];
  if (adminPrefixRoutes.includes(dbRoute)) return `/admin${dbRoute}`;
  if (dbRoute === '/dashboard') return '/';
  return dbRoute;
};

// SVG icon components for sidebar menu items (keyed by DB page_route)
const routeIcons = {
  '/dashboard': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="2" />
      <path d="M13 7L17 7M13 11L17 11M7 15L17 15" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/tickets': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M8 7H16M8 12H16M8 17H12" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/regions': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/branches': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M17 10L21 6M21 6L17 2M21 6H9M7 14L3 18M3 18L7 22M3 18H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/categories': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 7V5C3 3.89543 3.89543 3 5 3H9L11 5H19C20.1046 5 21 5.89543 21 7V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7Z" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/teams': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M3 21V19C3 16.7909 4.79086 15 7 15H11C13.2091 15 15 16.7909 15 19V21" stroke="currentColor" strokeWidth="2" />
      <path d="M16 11C17.6569 11 19 9.65685 19 8C19 6.34315 17.6569 5 16 5" stroke="currentColor" strokeWidth="2" />
      <path d="M18 21V19C18 17.3431 17.3284 15.8579 16.2428 14.8145" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/tags': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z" stroke="currentColor" strokeWidth="2" />
      <circle cx="7" cy="7" r="1.5" fill="currentColor" />
    </svg>
  ),
  '/statuses': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/escalations': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/sender-emails': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 7l9 7 9-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/users': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" />
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" />
      <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/equipment': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 12H7L9 9L12 15L14 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/roles': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" />
      <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  '/reports': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 16l4-8 4 4 4-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/reports/category': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 7V5C3 3.89543 3.89543 3 5 3H9L11 5H19C20.1046 5 21 5.89543 21 7V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7Z" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/reports/region': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/reports/team': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M3 21V19C3 16.7909 4.79086 15 7 15H11C13.2091 15 15 16.7909 15 19V21" stroke="currentColor" strokeWidth="2" />
      <path d="M17 11l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/reports/equipment': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 12H7L9 9L12 15L14 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/reports/sla': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  '/reports/it-team': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M3 21V19C3 16.7909 4.79086 15 7 15H11C13.2091 15 15 16.7909 15 19V21" stroke="currentColor" strokeWidth="2" />
      <path d="M16 11C17.6569 11 19 9.65685 19 8C19 6.34315 17.6569 5 16 5" stroke="currentColor" strokeWidth="2" />
      <path d="M18 21V19C18 17.3431 17.3284 15.8579 16.2428 14.8145" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/reports/weekly': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M9 17H15M9 13H15M9 9H10M13 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V11L13 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 3V9C13 10.1046 13.8954 11 15 11H21" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  '/config': (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
};

// Fallback icon
const defaultIcon = (size) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const getIcon = (dbRoute, size) => {
  const iconFn = routeIcons[dbRoute];
  return iconFn ? iconFn(size) : defaultIcon(size);
};

const Sidebar = () => {
  const [awaitingCount, setAwaitingCount] = useState(0);
  const { user, logout } = useAuth();

  // The assignment queue is a BioMedical idea: a coordinator is emailed to put
  // an engineer on each new ticket. IT routes through teams and has never had
  // this badge, so it stays off there even though IT roles may assign.
  const showAssignmentQueue = (user?.department_code || 'IT') === 'BM';

  useEffect(() => {
    if (!showAssignmentQueue) { setAwaitingCount(0); return; }
    let cancelled = false;
    const load = () => ticketService.getAwaitingAssignmentCount()
      .then((res) => { if (!cancelled) setAwaitingCount(res.data?.count || 0); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [showAssignmentQueue]);
  const { isCollapsed, setIsCollapsed, isMobile, isMobileOpen, setIsMobileOpen } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  // Track which parent menus are expanded (keyed by parent page_id)
  const [expandedParents, setExpandedParents] = useState(() => {
    const ids = {};
    if (user?.permissions) {
      user.permissions.forEach(p => {
        if (p.parent_id && location.pathname.startsWith(getFrontendRoute(p.page_route))) {
          ids[p.parent_id] = true;
        }
      });
    }
    return ids;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/dashboard';
    if (path === '/tickets') return location.pathname === '/tickets';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleParent = (pageId) => {
    setExpandedParents(prev => ({ ...prev, [pageId]: !prev[pageId] }));
  };

  // Build menu structure from permissions
  const { mainItems, adminItems } = useMemo(() => {
    const permissions = user?.permissions || [];
    const main = [];
    const admin = [];

    const topLevel = permissions.filter(p => p.parent_id === null);
    const children = permissions.filter(p => p.parent_id !== null);

    // Group children by parent_id
    const childrenByParent = {};
    children.forEach(c => {
      if (!childrenByParent[c.parent_id]) childrenByParent[c.parent_id] = [];
      childrenByParent[c.parent_id].push(c);
    });

    // Sort top level by priority
    topLevel.sort((a, b) => (a.priority || 0) - (b.priority || 0));

    // Dashboard and Tickets go in "Main", everything else in "Administration"
    const mainRoutes = ['/dashboard', '/tickets'];

    topLevel.forEach(page => {
      const hasChildren = !!childrenByParent[page.page_id];
      const item = {
        ...page,
        frontendRoute: getFrontendRoute(page.page_route),
        children: hasChildren
          ? childrenByParent[page.page_id]
              .sort((a, b) => (a.priority || 0) - (b.priority || 0))
              .filter(c => c.can_list || c.can_view)
              .map(c => ({
                ...c,
                frontendRoute: getFrontendRoute(c.page_route),
              }))
          : [],
      };

      if (mainRoutes.includes(page.page_route)) {
        if (page.can_list || page.can_view) main.push(item);
      } else {
        if (hasChildren) {
          if (item.children.length > 0) admin.push(item);
        } else if (page.can_list || page.can_view) {
          admin.push(item);
        }
      }
    });

    return { mainItems: main, adminItems: admin };
  }, [user?.permissions]);

  if (!user) return null;

  const iconSize = isCollapsed ? '24' : '20';

  const renderMenuItem = (item) => {
    const route = item.frontendRoute;
    return (
      <Link
        key={item.page_id}
        to={route}
        className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg transition-colors ${
          isActive(route)
            ? 'bg-primary-600 text-white'
            : 'text-gray-300 hover:bg-gray-800'
        }`}
        title={isCollapsed ? item.page_name : ''}
        onClick={(e) => { e.stopPropagation(); if (isMobile) setIsMobileOpen(false); }}
      >
        {getIcon(item.page_route, iconSize)}
        {!isCollapsed && <span className="flex-1">{item.page_name}</span>}
        {item.page_route === '/tickets' && awaitingCount > 0 && (
          <span
            title={`${awaitingCount} ticket${awaitingCount === 1 ? '' : 's'} awaiting assignment`}
            className={`${isCollapsed ? 'absolute top-1 right-1' : ''} min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-semibold flex items-center justify-center`}
          >
            {awaitingCount > 99 ? '99+' : awaitingCount}
          </span>
        )}
      </Link>
    );
  };

  const renderParentItem = (item) => {
    const isExpanded = !!expandedParents[item.page_id];
    const hasActiveChild = item.children.some(c => isActive(c.frontendRoute));

    return (
      <div key={item.page_id}>
        <button
          className={`flex items-center w-full ${isCollapsed ? 'justify-center' : 'gap-3 justify-between'} px-3 py-2 rounded-lg transition-colors ${
            hasActiveChild
              ? 'bg-primary-600 text-white'
              : 'text-gray-300 hover:bg-gray-800'
          }`}
          title={isCollapsed ? item.page_name : ''}
          onClick={(e) => {
            e.stopPropagation();
            if (isCollapsed) {
              setIsCollapsed(false);
              setExpandedParents(prev => ({ ...prev, [item.page_id]: true }));
            } else {
              toggleParent(item.page_id);
            }
          }}
        >
          <div className={`flex items-center ${isCollapsed ? '' : 'gap-3'}`}>
            {getIcon(item.page_route, iconSize)}
            {!isCollapsed && <span>{item.page_name}</span>}
          </div>
          {!isCollapsed && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            >
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>

        {!isCollapsed && isExpanded && (
          <div className="ml-4 mt-1 space-y-0.5 border-l border-gray-700 pl-3">
            {item.children.map(child => (
              <Link
                key={child.page_id}
                to={child.frontendRoute}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isActive(child.frontendRoute)
                    ? 'bg-primary-600 bg-opacity-80 text-white'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                }`}
                onClick={(e) => { e.stopPropagation(); if (isMobile) setIsMobileOpen(false); }}
              >
                {getIcon(child.page_route, '14')}
                <span>{child.page_name}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-gray-900 text-white flex flex-col shadow-xl z-50 transition-all duration-300 cursor-pointer
        ${isMobile
          ? `w-64 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`
          : (isCollapsed ? 'w-16' : 'w-64')}`}
      onClick={() => { if (!isMobile) setIsCollapsed(!isCollapsed); }}
    >
      <div className={`border-b border-gray-800 flex items-center ${isCollapsed ? 'justify-center p-4' : 'justify-between p-6'}`}>
        {isCollapsed ? (
          <img
            src="/favicon.png"
            alt="Maxivision"
            className="w-8 h-8 object-contain cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setIsCollapsed(false);
            }}
            title="Expand Sidebar"
          />
        ) : (
          <div className="flex items-center bg-white bg-opacity-90 rounded-lg px-3 py-2 shadow-md border border-gray-600">
            <img
              src="/logo-new.png"
              alt="Maxivision Logo"
              className="h-12 w-auto object-contain"
              onError={(e) => {
                console.error('Sidebar logo failed to load');
                e.target.src = '/favicon.png';
              }}
            />
          </div>
        )}

        {!isCollapsed && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsCollapsed(!isCollapsed);
            }}
            className="p-1 rounded-md hover:bg-gray-800 transition-colors"
            title="Collapse Sidebar"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 custom-sidebar-scrollbar">
        {mainItems.length > 0 && (
          <div className={`px-3 mb-6 ${isCollapsed ? 'px-2' : ''}`}>
            {!isCollapsed && <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Main</h3>}
            {mainItems.map(item =>
              item.children.length > 0 ? renderParentItem(item) : renderMenuItem(item)
            )}
          </div>
        )}

        {adminItems.length > 0 && (
          <div className={`px-3 ${isCollapsed ? 'px-2' : ''}`}>
            {!isCollapsed && <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Administration</h3>}
            {adminItems.map(item =>
              item.children.length > 0 ? renderParentItem(item) : renderMenuItem(item)
            )}

            {user?.role_id === 1 && (
              <Link
                to="/reports/weekly"
                className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg transition-colors ${
                  isActive('/reports/weekly')
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
                title={isCollapsed ? 'Weekly Report' : ''}
                onClick={(e) => e.stopPropagation()}
              >
                {getIcon('/reports/weekly', iconSize)}
                {!isCollapsed && <span>Weekly Report</span>}
              </Link>
            )}
          </div>
        )}
      </nav>

      <div className={`p-4 border-t border-gray-800 ${isCollapsed ? 'px-2' : ''}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleLogout();
          }}
          className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors w-full`}
          title={isCollapsed ? 'Logout' : ''}
        >
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none">
            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" />
            <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 12H9" stroke="currentColor" strokeWidth="2" />
          </svg>
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
